import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAnalytics } from "@/contexts/AnalyticsContext";
import { AnalyticsFlowEvent } from "@/analytics/types";
import {
    MediaItem,
    MediaItemStatus,
    MediaItemUuid,
    MediaItemFile,
    MediaItemFileType
} from "@/types/media";
import {
    useCurrentWorkspace,
    useCurrentQrSession,
    useEnsureValidQrSession,
    useStartListeningToQrSession,
    useCurrentMedia,
    useMediaStore
} from "@/store";
import { TrimTimes } from "@/types/media";
import { MediaActions, MediaContext, MediaContextType, UploadErrorDetails } from "./MediaContext";
import { useFlowState } from "@/hooks/useFlowState";
import { Flows } from "@/types/flows";
import { useMediaUtils, filenameForFile } from "@/hooks/useMediaUtils";
import { v4 as uuidv4 } from "uuid";
import { DEFAULT_CONFIDENCE_THRESHOLD } from "@utils/predictions";
import { ApiError } from "@utils/api/utils";
import { UploadErrorModal } from "@/components/flow/shared/UploadErrorModal";
import { UploadLimitErrorType } from "@/constants/media";
import { validateUploadLimits } from "@/utils/mediaValidation";

export const MediaProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const currentWorkspace = useCurrentWorkspace();
    const { identity: analyticsIdentity, track } = useAnalytics();
    const { flow } = useFlowState();

    // media management
    const media = useMediaStore();
    const {
        setCurrentMedia,
        mediaItemFactoryGenerate,
        addMediaItem,
        updateMediaItem,
        extractFrameFileFromMediaItem,
        trimMediaItemFile,
        uploadMediaItem,
        deleteMediaItem: deleteMediaItemFromStore,
        getMediaItemCaptionProposals
    } = useMediaUtils();
    // current media management
    const currentMedia = useCurrentMedia();

    // QR Upload integration
    const currentQrSession = useCurrentQrSession();
    const ensureValidQrSession = useEnsureValidQrSession();
    const startListeningToQrSession = useStartListeningToQrSession();

    const [mediaItemIds, setMediaItemIds] = useState<MediaItemUuid[]>([]);

    const mediaItems = useMemo(
        () => mediaItemIds.map((id) => media[id]).filter(Boolean),
        [mediaItemIds, media]
    );

    const pendingMediaItems = useMemo(() => {
        return mediaItems.filter(
            (item) => item.status === MediaItemStatus.PENDING && !!item.validation
        );
    }, [mediaItems]);

    const completedMediaItems = useMemo(() => {
        return mediaItems.filter((item) => item.status === MediaItemStatus.COMPLETED);
    }, [mediaItems]);

    const erroredMediaItems = useMemo(() => {
        return mediaItems.filter((item) => item.status === MediaItemStatus.ERROR);
    }, [mediaItems]);

    // Include upload queue items in the display
    // Preserve original upload order from mediaItemIds, not grouped by status
    const orderedMediaItems = useMemo(() => {
        return mediaItems.filter((item) => item.status !== MediaItemStatus.ERROR);
    }, [mediaItems]);

    // Track visited files for auto-prediction generation
    const [visitedFileIds, setVisitedFileIds] = useState<Set<string>>(new Set());

    // Stable text prompt proposals (generated from first uploaded file)
    const [stableTextPromptProposals, setStableTextPromptProposals] = useState<
        string[] | undefined
    >(undefined);
    const [stableTextPromptProposalFileId, setStableTextPromptProposalFileId] = useState<
        string | null
    >(null);
    const isGeneratingStableProposalsRef = React.useRef(false);

    // trim management
    const [mediaItemToTrimId, setMediaItemToTrimId] = useState<MediaItemUuid | undefined>();
    const [currentTrimTimes, setCurrentTrimTimes] = useState<TrimTimes | null>(null);

    // upload error management
    const [uploadError, setUploadError] = useState<UploadErrorDetails | undefined>(undefined);
    const [uploadLimitError, setUploadLimitError] = useState<UploadLimitErrorType | null>(null);

    const mediaItemToTrim = useMemo(() => {
        return mediaItemToTrimId ? media[mediaItemToTrimId] : undefined;
    }, [mediaItemToTrimId, media]);

    const storageUploadDir: string = useMemo(() => {
        if (!currentWorkspace) return "anonymous";

        return currentWorkspace.url as string;
    }, [currentWorkspace]);

    const deleteMediaItem = useCallback(
        async (id: string) => {
            const mediaItem = mediaItems.find((item) => item.id === id);
            if (!mediaItem) return;

            // Check if we're deleting the currently viewed file
            const isDeletingCurrentFile = currentMedia?.file.id === id;

            // If deleting current file and there are other files, navigate to next/previous
            if (isDeletingCurrentFile && orderedMediaItems.length > 1) {
                const currentIndex = orderedMediaItems.findIndex((item) => item.id === id);

                // Try to navigate to the next file, or previous if this is the last file
                const nextIndex =
                    currentIndex < orderedMediaItems.length - 1
                        ? currentIndex + 1
                        : currentIndex - 1;

                const targetFile = orderedMediaItems[nextIndex];

                if (targetFile) {
                    // Set the next file as current before deletion
                    const updatedCurrentMedia: {
                        file: MediaItem;
                        frame?: MediaItem;
                        frameSegment?: number;
                    } = { file: targetFile };

                    if (targetFile.isVideo) {
                        const frame0 = media[targetFile.frame0Id!];
                        if (frame0) {
                            updatedCurrentMedia.frame = frame0;
                            updatedCurrentMedia.frameSegment = 0;
                        } else {
                            updatedCurrentMedia.frameSegment = 0;
                        }
                    } else {
                        updatedCurrentMedia.frame = targetFile;
                        updatedCurrentMedia.frameSegment = 0;
                    }

                    setCurrentMedia(updatedCurrentMedia);
                }
            }

            // ideally the below would all be handled in a transaction
            await deleteMediaItemFromStore(mediaItem.id, { keepInStorage: flow === Flows.SHARE });

            setMediaItemIds((prev) => prev.filter((id) => id !== mediaItem.id));
        },
        [
            mediaItems,
            currentMedia,
            orderedMediaItems,
            media,
            flow,
            deleteMediaItemFromStore,
            setCurrentMedia
        ]
    );

    // === upload queue helpers ===
    const addMediaItems = useCallback(
        async (files: MediaItemFile[]) => {
            // Validate upload limits before processing
            const limitError = validateUploadLimits(files);
            if (limitError) {
                setUploadLimitError(limitError);
                return [];
            }

            // Clear any previous upload limit errors
            setUploadLimitError(null);

            // Step 1: Create placeholder media items with PROCESSING status
            const placeholderItems = files.map((file) => {
                const isVideo =
                    file.type.startsWith("video/") ||
                    (() => {
                        const extension = `.${file.name.split(".").pop()?.toLowerCase()}`;
                        const videoExtensions = [
                            ".mp4",
                            ".webm",
                            ".mov",
                            ".avi",
                            ".mkv",
                            ".wmv",
                            ".flv",
                            ".m4v",
                            ".3gp",
                            ".3g2",
                            ".ogv",
                            ".ts",
                            ".mts",
                            ".asf",
                            ".divx",
                            ".dv",
                            ".rv",
                            ".vob"
                        ];
                        return videoExtensions.includes(extension);
                    })();

                return {
                    id: file.id || uuidv4(),
                    file,
                    fileType: isVideo ? MediaItemFileType.VIDEO : MediaItemFileType.IMAGE,
                    status: MediaItemStatus.PROCESSING,
                    isTestFile: false,
                    name: filenameForFile(file),
                    storageUploadDirOverride: file.storageUploadDirOverride,
                    progress: 0,
                    confidenceThreshold: DEFAULT_CONFIDENCE_THRESHOLD,
                    isVideo,
                    metadata: { frame_count: 1, clamped_time: 0, segment: 0, keyframe_offset: 0 }
                } as MediaItem;
            });

            // Add placeholders to store and UI
            setMediaItemIds((prev) => {
                const newItems = placeholderItems
                    .filter((newItem) => !prev.some((id) => id === newItem.id))
                    .map(({ id }) => id);

                if (newItems.length === 0) {
                    return prev;
                }

                return [...prev, ...newItems];
            });

            placeholderItems.forEach((item) => {
                addMediaItem(item);
            });

            // Step 2: Initialize metadata in background (re-encoding if needed)
            // Process in background without awaiting so modal can close immediately after validation
            Promise.all(
                placeholderItems.map(async (placeholderItem) => {
                    try {
                        const mediaItem = await mediaItemFactoryGenerate({
                            file: placeholderItem.file,
                            ...placeholderItem
                        });
                        updateMediaItem(mediaItem);
                        return mediaItem;
                    } catch (err) {
                        console.error("🐞[DEBUG - addMediaItems] Error creating queue item", err);

                        if (err instanceof ApiError) {
                            setUploadError({
                                errorCode: err.errorCode,
                                message: err.message,
                                metadata: err.metadata
                            });
                        } else {
                            setUploadError({
                                message:
                                    err instanceof Error ? err.message : "Failed to process file"
                            });
                        }

                        deleteMediaItemFromStore(placeholderItem.id);
                        setMediaItemIds((prev) => prev.filter((id) => id !== placeholderItem.id));

                        return null;
                    }
                })
            ).catch((err) => {
                console.error("Error processing media items in background:", err);
            });

            // Return placeholders immediately (validation passed)
            return placeholderItems;
        },
        [
            addMediaItem,
            mediaItemFactoryGenerate,
            updateMediaItem,
            deleteMediaItemFromStore,
            setMediaItemIds,
            setUploadError
        ]
    );

    const triggerUpload = useCallback(
        async (mediaItem: MediaItem, batchIndex: number) => {
            // currently, for every new batch of uploads, we set the first
            // media item as the current media
            const setAsCurrentMedia = batchIndex === 0;

            mediaItem = { ...mediaItem, status: MediaItemStatus.UPLOADING };

            updateMediaItem(mediaItem);

            try {
                if (mediaItem.isVideo) {
                    // extract and upload first frame
                    let frame0MediaItem = await extractFrameFileFromMediaItem({
                        mediaItem,
                        targetTime: mediaItem.metadata?.keyframe_offset || 0
                    });

                    if (!frame0MediaItem) {
                        console.warn(
                            "[MediaProvider.triggerUpload] 🎬 [INITIALIZE FRAME 0] No frame 0 found",
                            mediaItem
                        );
                        return;
                    }

                    const uploadedFrame0MediaItem = await uploadMediaItem(frame0MediaItem, {
                        storageUploadDir
                    });

                    if (setAsCurrentMedia) {
                        setCurrentMedia(
                            { file: mediaItem, frameSegment: 0, frame: uploadedFrame0MediaItem },
                            { skipIfSet: true }
                        );
                    }

                    const uploadedMediaItem = await uploadMediaItem(mediaItem, {
                        storageUploadDir
                    });

                    if (setAsCurrentMedia) {
                        setCurrentMedia(
                            {
                                file: uploadedMediaItem,
                                frameSegment: 0,
                                frame: uploadedFrame0MediaItem
                            },
                            { skipIfSet: true }
                        );
                    }
                } else {
                    const uploadedMediaItem = await uploadMediaItem(mediaItem, {
                        storageUploadDir
                    });

                    if (setAsCurrentMedia) {
                        setCurrentMedia(
                            { file: uploadedMediaItem, frameSegment: 0, frame: uploadedMediaItem },
                            { skipIfSet: true }
                        );
                    }
                }

                track({
                    event: AnalyticsFlowEvent.FILE_UPLOADED,
                    data: {
                        format: mediaItem.file.type,
                        size_bytes: mediaItem.file.size,
                        size_mb: mediaItem.file.size / (1024 * 1024),
                        name: mediaItem.uploadResult?.filename || mediaItem.name,
                        uuid: mediaItem.id,
                        ...(mediaItem.metadata ?? {})
                    }
                });
            } catch (err) {
                console.error("Upload failed:", err);
                updateMediaItem({
                    ...mediaItem,
                    error: new Error(
                        (err as Error)?.message || `Upload failed for media item ${mediaItem.id}`
                    ),
                    status: MediaItemStatus.ERROR
                });
            }
        },
        [
            storageUploadDir,
            track,
            extractFrameFileFromMediaItem,
            updateMediaItem,
            setCurrentMedia,
            uploadMediaItem
        ]
    );

    const processMediaItemsQueue = useCallback(async () => {
        if (pendingMediaItems.length === 0) return;

        const candidateIDs = pendingMediaItems.map(({ id }) => id);

        const { readyForUpload, needsTrimming, invalid } = pendingMediaItems.reduce(
            (acc, mediaItem) => {
                if (!mediaItem.validation?.isValid) {
                    acc.invalid.push(mediaItem);
                } else if (mediaItem.validation?.needsTrimming) {
                    acc.needsTrimming.push(mediaItem);
                } else {
                    acc.readyForUpload.push(mediaItem);
                }
                return acc;
            },
            { readyForUpload: [], needsTrimming: [], invalid: [] } as Record<string, MediaItem[]>
        );

        // Prioritize the first file in original upload order that is not completed
        const firstNonCompletedFile = mediaItems.find(
            (item) =>
                item.status === MediaItemStatus.PENDING ||
                item.status === MediaItemStatus.UPLOADING ||
                item.status === MediaItemStatus.PROCESSING
        );

        // If the first file is already uploading, we can start others in parallel
        const isFirstFileUploading = firstNonCompletedFile?.status === MediaItemStatus.UPLOADING;

        // Prioritize trimming for the first file if it needs trimming
        if (needsTrimming.length > 0 && !mediaItemToTrimId) {
            const prioritizedTrimFile = firstNonCompletedFile
                ? needsTrimming.find((item) => item.id === firstNonCompletedFile.id)
                : null;
            const newMediaItemToTrim = prioritizedTrimFile || needsTrimming[0];
            updateMediaItem({ ...newMediaItemToTrim, status: MediaItemStatus.PROCESSING });
            setMediaItemToTrimId(newMediaItemToTrim.id);
        }

        const prioritizedFile =
            firstNonCompletedFile &&
            readyForUpload.find((item) => item.id === firstNonCompletedFile.id);
        const otherReadyFiles = readyForUpload.filter(
            (item) => !prioritizedFile || item.id !== prioritizedFile.id
        );

        if (
            readyForUpload.length > 0 ||
            invalid.length > 0 ||
            needsTrimming?.[0]?.status === MediaItemStatus.PROCESSING
        ) {
            // need to do a single status update to avoid retriggering the upload queue
            // make sure to include items that are not candidates for processing

            // NOTE: only update status if there are new items to process
            //  - newly errored items
            //  - newly uploadable items
            //  - items that need trimming AND we aren't currently trimming...avoid queue updates that would retrigger process for pending trim candidates that are queued
            setMediaItemIds((prev) => {
                const others = prev.filter((id) => !candidateIDs.includes(id));

                return [
                    ...others,
                    ...readyForUpload.map(({ id }) => id),
                    ...needsTrimming.map(({ id }) => id),
                    ...invalid.map(({ id }) => id)
                ];
            });
        }

        // Upload prioritized file first, then others in parallel
        // Only start uploads if first file is ready or already uploading
        const shouldStartUploads =
            !firstNonCompletedFile || prioritizedFile || isFirstFileUploading;

        if (shouldStartUploads) {
            if (prioritizedFile) {
                // Start prioritized file upload first
                const prioritizedUploadPromise = triggerUpload(prioritizedFile, 0);

                // Wait longer to ensure the upload actually starts and gets bandwidth priority
                await new Promise((resolve) => setTimeout(resolve, 500));

                // Then upload others in parallel (don't wait for prioritized file to finish)
                if (otherReadyFiles.length > 0) {
                    Promise.all(
                        otherReadyFiles.map((uI, index) => triggerUpload(uI, index + 1))
                    ).catch((err) => {
                        console.error("Other files upload failed:", err);
                    });
                }
                // Don't await prioritized file - let it finish in background
                prioritizedUploadPromise.catch((err) => {
                    console.error("Prioritized file upload failed:", err);
                });
            } else if (isFirstFileUploading && otherReadyFiles.length > 0) {
                // First file is already uploading, we can start others in parallel
                await Promise.all(otherReadyFiles.map((uI, index) => triggerUpload(uI, index)));
            } else if (otherReadyFiles.length > 0) {
                // If no first file exists, upload all in parallel
                await Promise.all(otherReadyFiles.map((uI, index) => triggerUpload(uI, index)));
            }
        }
        // If first file isn't ready yet, don't start uploads - wait for it to become ready
    }, [pendingMediaItems, triggerUpload, mediaItemToTrimId, updateMediaItem, mediaItems]);

    // Auto-process upload queue when items are added
    useEffect(() => {
        if (pendingMediaItems.length > 0) {
            processMediaItemsQueue();
        }
    }, [pendingMediaItems, processMediaItemsQueue]);

    // Generate stable text prompt proposals from first uploaded file
    useEffect(() => {
        // Reset proposals when all files are deleted
        if (!currentMedia?.frame && stableTextPromptProposals !== undefined) {
            setStableTextPromptProposals(undefined);
            setStableTextPromptProposalFileId(null);
            isGeneratingStableProposalsRef.current = false;
            return;
        }

        // Generate proposals from first file if not already generated
        if (
            currentMedia?.frame &&
            !stableTextPromptProposals &&
            !isGeneratingStableProposalsRef.current
        ) {
            isGeneratingStableProposalsRef.current = true;

            const abortController = new AbortController();

            getMediaItemCaptionProposals(currentMedia.frame, { abortController })
                .then((proposals) => {
                    if (proposals && proposals.length > 0) {
                        setStableTextPromptProposals(proposals);
                        setStableTextPromptProposalFileId(currentMedia.frame?.id!);
                    } else {
                        setStableTextPromptProposals([]);
                        setStableTextPromptProposalFileId(currentMedia.frame?.id!);
                    }
                })
                .catch((err) => {
                    console.error("Error generating stable text prompt proposals:", err);
                    setStableTextPromptProposals([]);
                    setStableTextPromptProposalFileId(currentMedia?.frame?.id!);
                })
                .finally(() => {
                    isGeneratingStableProposalsRef.current = false;
                });
        }
    }, [
        currentMedia?.frame,
        stableTextPromptProposals,
        stableTextPromptProposalFileId,
        getMediaItemCaptionProposals
    ]);

    // QR Upload session management
    useEffect(() => {
        // For authenticated users, use workspace URL
        if (currentWorkspace) {
            const workspaceUrl = currentWorkspace.url || currentWorkspace.id;
            if (workspaceUrl) {
                ensureValidQrSession(workspaceUrl);
            }
        } else {
            // For anonymous users, use analytics anonymous ID (Segment handles persistence)
            if (analyticsIdentity?.id) {
                const anonymousId = `anon_${analyticsIdentity.id}`;
                ensureValidQrSession(anonymousId);
            } else {
                // Fallback: create anonymous session without waiting for analytics
                ensureValidQrSession("anonymous");
            }
        }
    }, [currentWorkspace, analyticsIdentity, ensureValidQrSession]);

    // Start listening to QR session
    useEffect(() => {
        if (currentQrSession?.id) {
            startListeningToQrSession(currentQrSession.id);
        }
    }, [currentQrSession?.id, startListeningToQrSession]);

    // QR processing is now handled by individual components

    // trimming helpers
    const handleVideoTrim = useCallback<MediaActions["handleVideoTrim"]>(
        async (onComplete) => {
            if (!mediaItemToTrim || !currentTrimTimes) {
                console.error("No video trim state or current trim times");
                alert("Error trimming video. Please try again.");
                return;
            }

            try {
                // TRIM VIDEO
                const trimmedMediaItem = await trimMediaItemFile({
                    mediaItem: mediaItemToTrim,
                    startTime: currentTrimTimes.startTime,
                    endTime: currentTrimTimes.endTime
                });

                setMediaItemToTrimId(undefined);
                setCurrentTrimTimes(null);

                onComplete?.(trimmedMediaItem.file);

                return trimmedMediaItem.id;
            } catch (err) {
                console.error("Error trimming video:", err);

                alert("Error trimming video. Please try again.");
            }
        },
        [mediaItemToTrim, currentTrimTimes, trimMediaItemFile]
    );

    const handleVideoTrimCancel = useCallback(() => {
        setMediaItemToTrimId(undefined);
        setCurrentTrimTimes(null);
        setCurrentMedia(undefined);
    }, [setMediaItemToTrimId, setCurrentTrimTimes, setCurrentMedia]);

    const rejectCurrentFile = useCallback(() => {
        if (mediaItemToTrimId) {
            // Remove the file that needs trimming
            setMediaItemIds((prev) => prev.filter((id) => id !== mediaItemToTrimId));
            // Clear trim state without clearing currentMedia to keep user on current page
            setMediaItemToTrimId(undefined);
            setCurrentTrimTimes(null);
        }
    }, [mediaItemToTrimId]);

    // Visited files management
    const markFileAsVisited = useCallback((fileId: string) => {
        setVisitedFileIds((prev) => new Set(prev).add(fileId));
    }, []);

    const isFileVisited = useCallback(
        (fileId: string) => {
            return visitedFileIds.has(fileId);
        },
        [visitedFileIds]
    );

    const clearUploadError = useCallback(() => {
        setUploadError(undefined);
    }, []);

    const clearUploadLimitError = useCallback(() => {
        setUploadLimitError(null);
    }, [setUploadLimitError]);

    const contextValue = useMemo<MediaContextType>(
        () => ({
            completedMediaItems,
            storageUploadDir,
            setMediaItemIds,
            deleteMediaItem,
            mediaItems,
            pendingMediaItems,
            erroredMediaItems,
            addMediaItems,
            mediaItemToTrimId,
            mediaItemToTrim,
            currentTrimTimes,
            setCurrentTrimTimes,
            handleVideoTrim,
            handleVideoTrimCancel,
            rejectCurrentFile,
            visitedFileIds,
            markFileAsVisited,
            isFileVisited,
            stableTextPromptProposals,
            orderedMediaItems,
            uploadError,
            setUploadError,
            clearUploadError,
            uploadLimitError,
            clearUploadLimitError
        }),
        [
            completedMediaItems,
            storageUploadDir,
            deleteMediaItem,
            mediaItems,
            pendingMediaItems,
            erroredMediaItems,
            addMediaItems,
            mediaItemToTrimId,
            mediaItemToTrim,
            currentTrimTimes,
            setCurrentTrimTimes,
            handleVideoTrim,
            handleVideoTrimCancel,
            rejectCurrentFile,
            visitedFileIds,
            markFileAsVisited,
            isFileVisited,
            stableTextPromptProposals,
            orderedMediaItems,
            uploadError,
            setUploadError,
            clearUploadError,
            uploadLimitError,
            clearUploadLimitError
        ]
    );

    return (
        <MediaContext.Provider value={contextValue}>
            {children}
            <UploadErrorModal
                isOpen={!!uploadError}
                onClose={clearUploadError}
                error={uploadError}
            />
        </MediaContext.Provider>
    );
};
